<script>
/**
 * StatusPill -- the canonical, accessible renderer of a state.
 *
 * Color carries the CATEGORY; icon + text label carry the SPECIFICS.
 * Always color + icon + text -- never color alone (UX-DR2/9/16); `label`
 * is REQUIRED so a pill can never render as colour/icon only.
 *
 * Color comes from the Story 1.1 design tokens (:root CSS custom
 * properties) -- never hardcode hexes (AC-8).
 */
const STATUSES = ["pending", "in-progress", "done", "failed", "warning"];

// Default MDI glyph per status. Overridable via the `icon` prop so callers
// can distinguish same-color done-states (truck vs receipt vs tag).
const DEFAULT_ICONS = {
  pending: "mdi-circle-outline",
  "in-progress": "mdi-loading",
  done: "mdi-check-circle",
  failed: "mdi-alert-circle",
  warning: "mdi-clock-outline"
};

export default {
  name: "StatusPill",
  props: {
    status: {
      type: String,
      default: "pending",
      validator: value => STATUSES.indexOf(value) !== -1
    },
    icon: {
      type: String,
      default: ""
    },
    label: {
      type: String,
      required: true
    }
  },
  computed: {
    // Guard against an out-of-enum status: the prop validator only warns in dev
    // and is stripped in production, so without this a stray value (e.g. an order
    // state) would render an unstyled `status-pill--<value>` with a misleading
    // pending glyph. Clamp once, use everywhere.
    normalizedStatus() {
      return STATUSES.indexOf(this.status) !== -1 ? this.status : "pending";
    },
    resolvedIcon() {
      return this.icon || DEFAULT_ICONS[this.normalizedStatus];
    },
    // Only the default in-progress glyph spins; a caller-supplied icon never does.
    // The spin is gated behind prefers-reduced-motion in CSS, and a static glyph
    // (mdi-timer-sand) is shown instead under reduced motion (AC-7) -- we do NOT
    // use the vendored `mdi-spin` class because its animation is unconditional.
    isSpinner() {
      return !this.icon && this.normalizedStatus === "in-progress";
    }
  }
};
</script>

<template>
  <span :class="['status-pill', `status-pill--${normalizedStatus}`]" role="status">
    <template v-if="isSpinner">
      <i class="mdi mdi-loading status-pill__icon status-pill__icon--spin" aria-hidden="true"></i>
      <i class="mdi mdi-timer-sand status-pill__icon status-pill__icon--static" aria-hidden="true"></i>
    </template>
    <i
      v-else
      :class="['mdi', resolvedIcon, 'status-pill__icon']"
      aria-hidden="true"
    ></i>
    <span class="status-pill__label">{{ label }}</span>
    <slot />
  </span>
</template>

<style scoped>
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-3);
  border: 1px solid transparent;
  border-radius: 50rem; /* fully rounded pill -- no --radius-pill token (Story 1.1) */
  background: var(--c-surface); /* fallback when color-mix() is unsupported */
  font-size: var(--t-caption); /* 12px / 600 */
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  vertical-align: middle;
}

.status-pill__icon {
  display: inline-flex;
  font-size: 1em;
  line-height: 1;
}

.status-pill__label {
  white-space: nowrap;
}

/* status -> colour: status label+icon text + border over a light tint of the
   same token (AC-1 "colour bg + border"). Tints/borders use the vivid brand
   `--c-*` hues; the LABEL+ICON TEXT uses the darker `--c-*-ink` tokens for the
   three hues (success/warning/danger) that fail AA at 12px on the tint, so the
   pill clears 4.5:1 as normal text (Story 1.6 D-1). neutral/primary already
   pass as text, so they keep using their base token for color. Tints are
   derived via color-mix (no hardcoded hex, AC-8); browsers without color-mix
   fall back to the plain --c-surface background set above. */
.status-pill--pending {
  color: var(--c-neutral);
  border-color: var(--c-neutral);
  background: color-mix(in srgb, var(--c-neutral) 10%, var(--c-surface));
}
.status-pill--in-progress {
  color: var(--c-primary);
  border-color: var(--c-primary);
  background: color-mix(in srgb, var(--c-primary) 10%, var(--c-surface));
}
.status-pill--done {
  color: var(--c-success-ink);
  border-color: var(--c-success);
  background: color-mix(in srgb, var(--c-success) 10%, var(--c-surface));
}
.status-pill--failed {
  color: var(--c-danger-ink);
  border-color: var(--c-danger);
  background: color-mix(in srgb, var(--c-danger) 10%, var(--c-surface));
}
.status-pill--warning {
  color: var(--c-warning-ink);
  border-color: var(--c-warning);
  background: color-mix(in srgb, var(--c-warning) 10%, var(--c-surface));
}

/* Reduced motion (AC-7): hide the spinning mdi-loading glyph and show a static
   mdi-timer-sand instead, so the in-progress state reads cleanly (no frozen
   partial circle). The caller's label still carries the specifics. */
.status-pill__icon--static {
  display: none;
}
@media (prefers-reduced-motion: no-preference) {
  .status-pill__icon--spin {
    animation: status-pill-spin 1s linear infinite;
  }
}
@media (prefers-reduced-motion: reduce) {
  .status-pill__icon--spin {
    display: none;
  }
  .status-pill__icon--static {
    display: inline-flex;
  }
}

@keyframes status-pill-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Visible 2px primary focus ring on any focusable trailing-slot link (AC-7).
   Uses the shared --focus-ring token (Story 1.6) — one source of truth. */
.status-pill ::v-deep a:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}
</style>
